import React, {
  FC,
  MouseEvent,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import clsx from 'clsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import isFunction from 'lodash/isFunction';
import PopulateLogo from 'core/icons/populateLogo';
import ToolTip from 'core/components/Tooltip';
import rootRoutes from 'pages/Dashboard/routes';
import {
  pccEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  usePCCAvailability,
} from 'views/EMR/hooks/pcc';
import usePersistentState from 'pages/Dashboard/hooks/usePersistentState';
import NavItem from 'pages/Dashboard/components/NavItem';
import Skeleton from 'core/components/Skeleton';
import {
  UserSettingsContext,
} from 'pages/Dashboard/contexts/userSettings';
import isNil from 'lodash/isNil';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';

type SidebarIconProps = {
  className?: string;
  data?: any;
};

export default function Sidebar() {
  const isSmallScreen = useMediaQuery('(max-width:1300px)');
  const { loadingAppSettings: appIsLoading } = useContext(UserSettingsContext);
  const [expanded, setExpanded] = usePersistentState<boolean>(true, 'sidebar.expanded');
  const isExpanded = useMemo(() => expanded && !isSmallScreen, [isSmallScreen, expanded]);
  const {
    canPath,
    userInfo,
  } = useEMRAccess();
  const {
    isLoading: isLoadingPCCAvailability,
    isPCCAvailable,
    isPCCPending,
  } = usePCCAvailability();
  const isUserInfoReady = !isNil(userInfo);
  const containerClass = clsx(
    'h-full overflow-hidden flex flex-col p-2 bg-primary',
    'transition-width duration-100 ease-in w-[12.5rem] small:!w-[3.75rem]',
    { 'w-[3.75rem]': !isExpanded },
  );
  const labelClass = clsx(
    'text-xs opacity-1 truncate transition-opacity duration-75 delay-100 ease-out',
    { 'opacity-0 text-[0px] collapse': !isExpanded },
  );

  const MenuIcon = useMemo(() => (
    isExpanded
      ? KeyboardDoubleArrowLeftRoundedIcon
      : KeyboardDoubleArrowRightRoundedIcon
  ), [isExpanded]);
  const visibleRoutes = useMemo(() => (
    isUserInfoReady
      ? rootRoutes.filter((route) => (
        canPath(`/${route.href}`)
        && (isFunction(route.isVisible) ? route.isVisible(userInfo ?? {}) : true)
      ))
      : []
  ), [canPath, isUserInfoReady, userInfo]);

  useEffect(() => {
    document.getElementById('root')?.classList.toggle('is-expanded', isExpanded);
  }, [isExpanded]);

  return (
    <div className="h-full relative sidebar">
      <div className={containerClass}>
        <div className="flex w-full overflow-hidden relative h-10 justify-center items-center mt-1 mb-2.5">
          <div className="overflow-hidden transition-width duration-100 ease-in absolute left-2.5">
            <PopulateLogo className="!w-40 !h-auto" />
          </div>
        </div>
        <div className="flex flex-auto flex-col justify-between overflow-hidden mt-1">
          <div className="flex flex-col overflow-hidden">
            {visibleRoutes
              .map((route) => {
                const hasComponent = typeof route.Component !== 'undefined';
                const Icon = (route.Component ?? route.Icon) as FC<SidebarIconProps>;
                const title = route.title ?? route.name;
                const isPCCRoute = route.href === 'pcc';

                return (
                  <ToolTip
                    key={route.href}
                    title={title}
                    hide={isExpanded}
                    placement="right"
                  >
                    <div className={clsx(route?.className, '!overflow-hidden')}>
                      <NavItem
                        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                          if (isPCCRoute && !isPCCAvailable) {
                            event.preventDefault();
                            if (!isLoadingPCCAvailability) {
                              pccEventChannel.emit('onShowPCCWalkthrough', {
                                initialPageId: isPCCPending ? 'pending' : 'overview',
                              });
                            }
                          }
                          route.onClick?.(event);
                        }}
                        key={route.name}
                        className="mb-0.5 space-x-2.5 !overflow-hidden"
                        href={`/${route.href}`}
                      >
                        {appIsLoading && isUserInfoReady
                          ? (
                            <div className="flex items-center gap-2.5 w-full">
                              <Skeleton variant="rounded" className="!w-5 !h-5 !bg-gray-100" />
                              {isExpanded && <Skeleton className="!h-5 flex-1 !bg-gray-100" />}
                            </div>
                          )
                          : (
                            <>
                              <Icon
                                className="!w-5 !h-5"
                                data={{ expanded: isExpanded }}
                              />
                              {!hasComponent && (
                                <span className={labelClass}>
                                  {title}
                                </span>
                              )}
                            </>
                          )}
                      </NavItem>
                    </div>
                  </ToolTip>
                );
              })}
          </div>
          {!isSmallScreen && (
            <div className="flex justify-between !overflow-hidden w-full">
              <ToolTip
                key={expanded.toString()}
                title="Expand Sidebar"
                hide={expanded}
                placement="right"
              >
                <div className="w-full">
                  <NavItem
                    className="text-populate-gray mb-0.5 space-x-2.5 w-full"
                    onClick={(e) => {
                      e.preventDefault();
                      setExpanded((expanded) => !expanded);
                    }}
                    activeClassName="!bg-gray-800"
                  >
                    <MenuIcon className="text-white !text-lg" />
                    <span className={labelClass}>
                      {expanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
                    </span>
                  </NavItem>
                </div>
              </ToolTip>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
